/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { Card } from '../../../common/Card';
import { TimestampLabel } from '../../../common/TimestampLabel';
import { Activity } from '../../../../types/activity.types';
import { getActivityIcon, getActivityIconBg } from '../../../../utils/activity/activityHelpers';
import {
  ActivityInlineBadges,
  ActivityDescriptionBlock,
  buildWebBadge,
  buildEmailBadge,
} from '../../../common/ActivityContent';

interface ActivityItemProps {
  activity: Activity;
  isNewlyAdded: boolean;
  onSelectCall: (call: any) => void;
  onSelectEmail?: (email: any) => void;
  /** SMS rows have no inline detail surface in the contact tab today;
   *  pass this to open a focused thread view (typically SmsThreadModal
   *  scoped to the contact). The "Open thread →" hover affordance fires
   *  it; the whole row click does too when provided. */
  onSelectSms?: (sms: any) => void;
  timezone?: string;
}

export const ActivityItem: React.FC<ActivityItemProps> = ({
  activity,
  isNewlyAdded,
  onSelectCall,
  onSelectEmail,
  onSelectSms,
  timezone,
}) => {
  const emailMeta = useMemo(() => {
    if (activity.type !== 'email' || !activity.sourceData?.metadata) return null;
    try {
      const parsed =
        typeof activity.sourceData.metadata === 'string'
          ? JSON.parse(activity.sourceData.metadata)
          : activity.sourceData.metadata;
      if (!parsed) return null;
      return {
        bodyHtml: (parsed.bodyHtml || parsed.body_html || null) as string | null,
        subject: (parsed.subject as string | undefined) || undefined,
        fromAddress: (parsed.fromAddress || parsed.from_address || parsed.from) as
          string | undefined,
        toAddress: (parsed.toAddress || parsed.to_address || parsed.to) as string | undefined,
        templateName: (parsed.templateName || parsed.template_name || parsed.template) as
          string | undefined,
        sentAt: (parsed.sentAt || parsed.sent_at) as string | undefined,
        emailKind: (parsed.emailKind as string | null | undefined) ?? null,
        conversationId: (parsed.conversationId ||
          parsed.conversation_id ||
          activity.sourceData.conversationId) as string | undefined,
        direction: (parsed.direction || activity.sourceData.direction) as string | undefined,
      };
    } catch {
      return null;
    }
  }, [activity.type, activity.sourceData]);

  const webBadge = useMemo(() => {
    if (activity.type !== 'website' || !activity.sourceData?.event_type) return undefined;
    return buildWebBadge(
      activity.sourceData.event_type,
      activity.sourceData.event_category,
      activity.sourceData.page_url
    );
  }, [activity.type, activity.sourceData]);

  // A Remi conversation reads like SMS (sent / received), without a delivery status.
  const emailBadge = useMemo(
    () =>
      activity.type === 'email' && emailMeta?.emailKind !== 'AGENTIC'
        ? buildEmailBadge('email', activity.sourceData?.outcome)
        : undefined,
    [activity.type, activity.sourceData?.outcome, emailMeta?.emailKind]
  );

  const handleClick = () => {
    if (activity.type === 'call' && activity.sourceData) {
      onSelectCall(activity.sourceData);
    } else if (activity.type === 'email' && onSelectEmail && activity.sourceData) {
      onSelectEmail({
        ...activity.sourceData,
        ...(emailMeta ?? {}),
      });
    } else if (activity.type === 'sms' && onSelectSms && activity.sourceData) {
      onSelectSms(activity.sourceData);
    }
  };

  const isClickable =
    activity.type === 'call' ||
    (activity.type === 'email' && !!onSelectEmail) ||
    (activity.type === 'sms' && !!onSelectSms);

  const icon = getActivityIcon(activity);
  const iconBg = getActivityIconBg(activity);

  return (
    <Card
      key={activity.id}
      // `group` so the inline hover-revealed actions (Open thread / View
      // call / Preview email inside ActivityInlineBadges) light up when
      // the row itself is hovered — matches the dashboard's
      // RecentActivities behaviour. The amber/nudge tint on the row uses
      // the same hover treatment as `.feed-row` so the two surfaces feel
      // identical to interact with.
      className={`group p-2.5 md:p-3 border-foreground/15 transition-all duration-200 ${
        isClickable
          ? 'cursor-pointer hover:bg-amber-400/[0.06] hover:translate-x-0.5 hover:shadow-md dark:hover:bg-amber-400/[0.04]'
          : ''
      } ${isNewlyAdded ? 'activity-item-highlight transition-colors duration-1500' : ''}`}
      onClick={handleClick}
    >
      <div className={`flex ${activity.description ? 'items-start' : 'items-center'}`}>
        <div className="mr-2.5 md:mr-3 mt-0.5">
          <div className={`${iconBg} rounded-full p-1.5 md:p-2`}>{icon}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="md:hidden mb-0.5">
            <TimestampLabel value={activity.timestamp} timezone={timezone} />
          </div>
          <div className="flex flex-wrap justify-between items-start gap-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="font-semibold text-[13px] text-foreground tracking-tight">
                {activity.title}
              </p>
              {/* The same hover actions as Recent Activities (View call / Open thread /
                  Preview email). They open what a click on the row opens, so the row
                  says what that click does. */}
              <ActivityInlineBadges
                eventType={activity.type}
                durationSeconds={activity.sourceData?.durationSeconds}
                outcome={activity.sourceData?.outcome}
                webBadge={webBadge}
                emailBadge={emailBadge}
                onViewCall={activity.type === 'call' && isClickable ? handleClick : undefined}
                onViewSms={activity.type === 'sms' && isClickable ? handleClick : undefined}
                onViewEmailThread={
                  activity.type === 'email' && isClickable && emailMeta?.emailKind === 'AGENTIC'
                    ? handleClick
                    : undefined
                }
                onViewEmail={
                  activity.type === 'email' && isClickable && emailMeta?.emailKind !== 'AGENTIC'
                    ? handleClick
                    : undefined
                }
                threadMessageCount={activity.sourceData?.threadMessageCount}
              />
            </div>
            <div className="hidden md:flex items-center">
              <TimestampLabel value={activity.timestamp} timezone={timezone} />
            </div>
          </div>
          <ActivityDescriptionBlock description={activity.description} />
        </div>
      </div>
    </Card>
  );
};
